import { useEffect, useRef } from 'react';
import { TranslationService } from '../../../../services/translation.service';
import type ICardFare from '../../../../models/tap-and-go/card-fare.model';
import { SanitizerService } from '../../../../services/sanitizer.service';
import vtLogo from '../../../../../assets/logos/vasttrafik-logo-black.svg';
import printIcon from '../../../../../assets/icons/print.svg';
import DataUrlHelper from '../../../../helpers/data-url.helper';
import type ICardCharge from '../../../../models/tap-and-go/card-charge.model';
import type ICard from '../../../../models/tap-and-go/card.model';
import Currency from '../../../Shared/Currency';
import './style.scss';
import { ConfigurationService } from '../../../../services/configuration.service';

function TapAndGoReceipt({ charge, card, onClose }: { charge: ICardCharge; card: ICard; onClose: () => void }) {
  const modalBodyRef = useRef<HTMLDivElement>(null);
  const modalContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClickOutside(event: MouseEvent) {
      if (modalContentRef.current && !modalContentRef.current.contains(event.target as Node)) {
        onClose();
      }
    }

    modalContentRef.current!.focus();
    setTimeout(() => {
      document.addEventListener('click', onClickOutside);
      document.getElementsByTagName('body')[0].classList.add('modal-open');
    }, 100);

    return () => {
      document.removeEventListener('click', onClickOutside);
      document.getElementsByTagName('body')[0].classList.remove('modal-open');
    };
  }, [onClose]);

  function getFareDescription(fare: ICardFare) {
    if (fare.description) {
      return fare.description;
    }
    if (fare.amountInOre < 0) {
      return TranslationService.get('tapandgoreceiptspage.refund');
    }
    return '';
  }

  function handleOnPrintButtonClick() {
    const printWindow = window.open('', '', 'height=600, width=600');
    const styleElements = window.document.head.querySelectorAll('[rel="stylesheet"]');
    const { title } = window.document;
    let styleHtml = '';
    for (let index = 0; index < styleElements.length; index += 1) {
      const element = styleElements[index];
      styleHtml += element.outerHTML;
    }

    printWindow?.document.write(`<!DOCTYPE html><html><head><title>${title}</title>`);
    printWindow?.document.write(styleHtml);
    printWindow?.document.write('</head><body class="p-2 print">');
    printWindow?.document.write(modalBodyRef.current!.innerHTML);

    printWindow?.document.write('</body></head>');
    printWindow?.document.close();
    setTimeout(() => {
      printWindow?.print();
    }, 100);
  }

  return (
    <div>
      <div className="modal d-block fade show tap-and-go-charge-receipt" aria-labelledby="receipt-heading" aria-describedby="receipt-description" aria-modal="true" role="dialog" tabIndex={-1}>
        <div className="modal-dialog modal-dialog-scrollable container tap-and-go-charge-receipt__modal-dialog">
          <div ref={modalContentRef} className="modal-content">
            <div className="modal-header border-0 d-flex justify-content-end pb-0">
              <button type="button" className="btn--as-link d-flex me-4" onClick={handleOnPrintButtonClick}>
                <span className="me-2 align-self-center">
                  <img src={DataUrlHelper.encode(printIcon)} alt="" className="d-block tap-and-go-charge-receipt__print-button-icon" />
                </span>
                <span className="align-self-center">{TranslationService.get('tapandgoreceiptspage.print')}</span>
              </button>
              <button type="button" className="btn--as-link" onClick={onClose}>
                {TranslationService.get('common.close')}
              </button>
            </div>
            <div ref={modalBodyRef} className="modal-body container">
              <img src={DataUrlHelper.encode(vtLogo)} alt="Västtrafik logo" className="mb-4 tap-and-go-charge-receipt__logo" />
              <h1 id="receipt-heading" className="h3 mb-0">
                {TranslationService.get('tapandgoreceiptspage.receipt')}
              </h1>
              <p id="receipt-description" className="mb-2">
                {TranslationService.get('tapandgoreceiptspage.receiptinformationtext')}
              </p>
              <div className="mb-2">
                <span className="fw-bold">{TranslationService.get('tapandgoreceiptspage.receiptdate')}:</span>&nbsp;<span>{charge.createdDateTime.toFormat('D')}</span>
              </div>
              <div className="mb-2">
                <div className="fw-bold">{TranslationService.get('tapandgoreceiptspage.seller')}:</div>
                <div dangerouslySetInnerHTML={{ __html: SanitizerService.sanitizeHtml(TranslationService.get('tapandgoreceiptspage.address')) }} />
              </div>
              <div className="mb-4">
                <div dangerouslySetInnerHTML={{ __html: SanitizerService.sanitizeHtml(TranslationService.get('tapandgoreceiptspage.contactinformation')) }} />
              </div>
              <div className="mb-2">
                <span className="fw-bold">{TranslationService.get('tapandgoreceiptspage.cardnumber')}:</span>&nbsp;<span>{card.cardNumber}</span>
              </div>
              {ConfigurationService.get('tapandgoreceiptspage.showcardreference') === 'true' && (
                <div className="mb-2">
                  <span className="fw-bold">{TranslationService.get('tapandgoreceiptspage.cardreference')}:</span>&nbsp;<span>{card.id}</span>
                </div>
              )}
              <div className="mb-2">
                <span className="fw-bold">{TranslationService.get('tapandgoreceiptspage.reference')}:</span>&nbsp;<span>{charge.id}</span>
              </div>
              <div className="table-responsive-sm">
                <table className="table mb-4">
                  <thead>
                    <tr>
                      <th scope="col">{TranslationService.get('tapandgoreceiptspage.date')}</th>
                      <th scope="col">{TranslationService.get('tapandgoreceiptspage.ticket')}</th>
                      <th scope="col">{TranslationService.get('tapandgoreceiptspage.amount')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {charge.fares.map((fare, i) => (
                      <tr key={i}>
                        <td>
                          <time dateTime={charge.createdDateTime.toFormat('D')}>{charge.createdDateTime.toFormat('D')}</time>
                        </td>
                        <td>{getFareDescription(fare)}</td>
                        <td>
                          <Currency amount={fare.amountInOre * 0.01} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={2} className="text-end pb-1 border-0">
                        <span className="fw-bold">{TranslationService.get('tapandgoreceiptspage.sumincludingtax')}</span>
                      </td>
                      <td className="pb-1 border-0">
                        <Currency amount={charge.amountInOre * 0.01} />
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={2} className="text-end border-0 pb-1 pt-0">
                        <span>{TranslationService.get('tapandgoreceiptspage.tax')}</span>
                      </td>
                      <td className="border-0 pb-1 pt-0">
                        <Currency amount={charge.taxInOre * 0.01} />
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={2} className="text-end border-0 pt-0">
                        <span>
                          ({TranslationService.get('tapandgoreceiptspage.excludingtax')} <Currency amount={charge.netAmountInOre * 0.01} />)
                        </span>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show"></div>
    </div>
  );
}

export default TapAndGoReceipt;
